import { Box, Theme, Tooltip, alpha, useTheme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useUserUid } from "hooks/settings";
import { useConfirmDialog } from "contexts/confirmDialog";
import { useCallback, useMemo } from "react";
import {
  ReviewPeriodData,
  useUnverifyPeriodMutation,
} from "src/store/api/generatedApi";
import { useTimelineZoom } from "../scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { getReviewPeriodColor } from "./utils/reviewPeriodColors";
import { bringInPeriodDisplayState } from "./utils/editScope";
import { useReviewSaveError } from "./hooks/useReviewSaveError";

dayjs.extend(utc);

// Base styling for a period bar shown mid-edit or mid-verify: an amber dashed box, the
// same shape whether or not it is currently pulsing.
const WARNING_DASHED_SX = {
  bgcolor: "warning.light",
  opacity: 0.6,
  border: "1px dashed",
  borderColor: "warning.main",
} as const;

const PERIOD_BAR_OPACITY = 0.4;

// Draws attention to a period a save is about to re-verify or that carries unsaved
// edits, so a reviewer scanning the timeline doesn't miss it.
const pulseModifiedPeriodSx = (theme: Theme) => ({
  "@keyframes pulseModifiedPeriod": {
    "0%, 100%": {
      boxShadow: `0 0 3px 1px ${alpha(theme.palette.info.main, 0.4)}`,
    },
    "50%": {
      boxShadow: `0 0 6px 2px ${alpha(theme.palette.info.main, 0.7)}`,
    },
  },
  animation: "pulseModifiedPeriod 2s ease-in-out infinite",
});

export const ManualReviewVideoPeriodBars = ({
  reviewPeriods,
  videoStart,
  isEditMode,
  modifiedPeriodIds,
  bringInWillVerifyPeriodIds,
}: {
  reviewPeriods: ReviewPeriodData[];
  videoStart: Dayjs;
  isEditMode?: boolean;
  modifiedPeriodIds?: Set<number>;
  // Set only inside the linked-period bring-in modal: the periods this save will verify.
  // Switches each bar to show whether confirming will verify it or it is just context.
  bringInWillVerifyPeriodIds?: readonly number[];
}) => {
  const state = useVideoControlState();
  const context = useTimelineZoom();
  const duration = context?.duration ?? state.duration;
  const visibleStart = context?.visibleStart ?? videoStart;
  const visibleEnd = context?.visibleEnd ?? videoStart.add(duration, "second");

  const tenantName = useTenant();
  const [unverifyPeriodMutation] = useUnverifyPeriodMutation();
  const { t } = useTenantTranslation();
  const confirm = useConfirmDialog();
  const reportReviewSaveError = useReviewSaveError();

  const handleUnverifyPeriod = useCallback(
    async (periodId: ReviewPeriodData["id"]) => {
      if (
        !(await confirm({
          title: t("confirm"),
          message: t("unverifyWarning"),
        }))
      ) {
        return;
      }
      try {
        await unverifyPeriodMutation({ tenantName, periodId }).unwrap();
      } catch (error) {
        await reportReviewSaveError(error);
      }
    },
    [unverifyPeriodMutation, tenantName, confirm, t, reportReviewSaveError]
  );

  const visiblePeriods = useMemo(() => {
    return reviewPeriods.filter((period) => {
      const periodStart = dayjs(period.start);
      const periodEnd = dayjs(period.end);
      return (
        periodEnd.isAfter(visibleStart) && periodStart.isBefore(visibleEnd)
      );
    });
  }, [reviewPeriods, visibleStart, visibleEnd]);

  if (visiblePeriods.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 6,
        display: "flex",
        overflow: "hidden",
      }}
    >
      {visiblePeriods.map((period) => (
        <ReviewPeriodBar
          key={period.id}
          period={period}
          videoStart={visibleStart}
          duration={duration}
          onUnverify={(periodId) => void handleUnverifyPeriod(periodId)}
          isEditMode={isEditMode}
          hasModifications={modifiedPeriodIds?.has(period.id) ?? false}
          bringInWillVerifyPeriodIds={bringInWillVerifyPeriodIds}
        />
      ))}
    </Box>
  );
};

const ReviewPeriodBar = ({
  period,
  videoStart,
  duration,
  onUnverify,
  isEditMode,
  hasModifications,
  bringInWillVerifyPeriodIds,
}: {
  period: ReviewPeriodData;
  videoStart: Dayjs;
  duration: number;
  onUnverify: (periodId: ReviewPeriodData["id"]) => void;
  isEditMode?: boolean;
  hasModifications?: boolean;
  bringInWillVerifyPeriodIds?: readonly number[];
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const userUid = useUserUid();
  const periodStart = useMemo(() => dayjs(period.start), [period.start]);
  const periodEnd = useMemo(() => dayjs(period.end), [period.end]);
  const leftPercent = (periodStart.diff(videoStart, "second") / duration) * 100;
  const widthPercent =
    ((periodEnd.diff(videoStart, "second") -
      periodStart.diff(videoStart, "second")) /
      duration) *
    100;

  const isVerified = period.verified_at != null;
  const verifiedAt = period.verified_at
    ? `${dayjs.utc(period.verified_at).format("YYYY-MM-DD HH:mm")} UTC`
    : null;
  const verifiedInfo =
    verifiedAt && period.verified_by_name
      ? `${t("verifiedBy", { name: period.verified_by_name })} · ${verifiedAt}`
      : verifiedAt;

  const positionSx = {
    position: "absolute",
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
    height: "100%",
    borderRadius: 0.5,
  } as const;

  // Inside the bring-in modal each bar shows what confirming will do to the period:
  // a verified period this save writes again is re-verified (amber, pulsing), a period
  // the save verifies is verified (solid brand), and everything else keeps the page's
  // own review-period colour at the page's own strength, so a neighbour the save leaves
  // alone still reads as verified, assigned or unassigned rather than as one flat block.
  if (bringInWillVerifyPeriodIds !== undefined) {
    const displayState = bringInPeriodDisplayState({
      isVerified,
      isEdited: hasModifications === true,
      willVerify: bringInWillVerifyPeriodIds.includes(period.id),
    });
    if (displayState === "reVerify") {
      return (
        <Tooltip title={t("willBeReVerified")} arrow>
          <Box
            sx={{
              ...positionSx,
              ...WARNING_DASHED_SX,
              ...pulseModifiedPeriodSx(theme),
            }}
          />
        </Tooltip>
      );
    }
    const willVerify = displayState === "verify";
    return (
      <Tooltip
        title={willVerify ? t("willBeVerified") : t("shownForContext")}
        arrow
      >
        <Box
          sx={{
            ...positionSx,
            ...(willVerify
              ? { bgcolor: "primary.main", opacity: 0.85 }
              : {
                  bgcolor: getReviewPeriodColor(period, userUid, "light"),
                  opacity: PERIOD_BAR_OPACITY,
                }),
          }}
        />
      </Tooltip>
    );
  }

  if (!isVerified) {
    return (
      <Box
        sx={{
          ...positionSx,
          bgcolor: getReviewPeriodColor(period, userUid, "light"),
          opacity: PERIOD_BAR_OPACITY,
        }}
      />
    );
  }

  if (isEditMode) {
    return (
      <Tooltip title={t("editing")} arrow>
        <Box
          sx={{
            ...positionSx,
            ...WARNING_DASHED_SX,
            ...(hasModifications && pulseModifiedPeriodSx(theme)),
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        verifiedInfo ? (
          <>
            {verifiedInfo}
            <br />
            {t("clickToUnverify")}
          </>
        ) : (
          t("clickToUnverify")
        )
      }
      arrow
    >
      <Box
        onClick={() => {
          onUnverify(period.id);
        }}
        sx={{
          ...positionSx,
          bgcolor: getReviewPeriodColor(period, userUid, "light"),
          opacity: PERIOD_BAR_OPACITY,
          cursor: "pointer",
          transition: "opacity 0.2s",
          "&:hover": { opacity: 0.7 },
        }}
      />
    </Tooltip>
  );
};
